import { z } from "zod";
import type { Priority } from "./enums.js";

/**
 * Gebouwdashboard op een scherm (docs/SCHERMEN.md): een alleen-lezen pagina zonder inloggen voor een tv in de
 * lobby of het beheerkantoor. Het profiel bepaalt welke gegevens de server meestuurt.
 */
export const SCREEN_PROFILES = ["lobby", "beheer"] as const;
export type ScreenProfile = (typeof SCREEN_PROFILES)[number];
export const SCREEN_PROFILE_LABELS: Record<ScreenProfile, string> = {
  lobby: "Lobby (publiek)",
  beheer: "Beheer (intern)",
};
export const SCREEN_PROFILE_HINTS: Record<ScreenProfile, string> = {
  lobby:
    "Voor bezoekers en huurders: wie er vandaag komt (zonder namen), storingen die je zelf op het scherm zet, aantallen meldingen en het weer.",
  beheer:
    "Voor de eigen ruimte: ook leveranciers en uitvoerders, taken, de Inbox, alle spoed- en hoge bevindingen en verlopen keuringen.",
};

/** Geldigheid van een schermlink in dagen; null = tot intrekken. */
export const SCREEN_DAYS = [30, 90, 365] as const;

export const screenCreateSchema = z.object({
  name: z.string().trim().min(1, "Geef het scherm een naam").max(80),
  profile: z.enum(SCREEN_PROFILES),
  days: z
    .union([z.literal(30), z.literal(90), z.literal(365)])
    .nullable()
    .default(null),
});
export type ScreenCreate = z.infer<typeof screenCreateSchema>;

/**
 * Koppelcode op de tv (app.klantpunt.io/scherm): 6 tekens zonder letters en cijfers die op elkaar lijken.
 * Een code is 10 minuten geldig en werkt één keer.
 */
export const PAIR_CODE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
export const PAIR_CODE_LENGTH = 6;
export const PAIR_CODE_MINUTES = 10;
/** Hoofdletters, zonder spaties of streepjes: "abc-123" wordt "ABC123". */
export const normalizePairCode = (v: string) => v.toUpperCase().replace(/[^A-Z0-9]/g, "");
export const pairCodeSchema = z
  .string()
  .transform(normalizePairCode)
  .pipe(z.string().length(PAIR_CODE_LENGTH, "De code op de tv heeft 6 tekens"));

export const screenPairSchema = screenCreateSchema.extend({ code: pairCodeSchema });

/** Antwoord aan de tv: de code om te tonen en het geheim om mee te vragen of hij al gekoppeld is. */
export interface ScreenPairingDto {
  code: string;
  secret: string;
  expiresAt: string;
}
export type ScreenPairingStatusDto = { state: "waiting" } | { state: "paired"; token: string };

/** Thema van het scherm: donker past het best bij een tv, licht bij een lichte ruimte. */
export const SCREEN_THEMES = ["dark", "light"] as const;
export type ScreenTheme = (typeof SCREEN_THEMES)[number];
export const SCREEN_THEME_LABELS: Record<ScreenTheme, string> = { dark: "Donker", light: "Licht" };
/** Nachtstand standaard van 19:00 tot 7:00; hele uren. */
export const SCREEN_NIGHT_DEFAULT = { from: 19, until: 7 } as const;

const hour = z.number().int().min(0).max(23);
export const screenUpdateSchema = z
  .object({
    name: z.string().trim().min(1, "Geef het scherm een naam").max(80),
    profile: z.enum(SCREEN_PROFILES),
    theme: z.enum(SCREEN_THEMES),
    /** null = nachtstand uit. */
    night: z
      .object({ from: hour, until: hour })
      .refine((n) => n.from !== n.until, "Kies een begin- en eindtijd die verschillen")
      .nullable(),
  })
  .partial();

/** Instellingen die de tv overneemt; in de lijst en in het antwoord aan het scherm. */
export interface ScreenSettingsDto {
  theme: ScreenTheme;
  night: { from: number; until: number } | null;
}

/** Bevinding op het scherm in de lobby: alleen met een eigen, publieke tekst. */
export const issueScreenSchema = z.object({
  text: z
    .string()
    .trim()
    .max(120)
    .nullable()
    .transform((v) => (v ? v : null)),
});

export interface IssueScreenDto {
  /** Tekst op het lobbyscherm; null = niet op het scherm. */
  text: string | null;
}

/** Scherm in de lijst bij het gebouw. Het token staat alleen in het antwoord op aanmaken. */
export interface ScreenDto {
  id: string;
  name: string;
  profile: ScreenProfile;
  settings: ScreenSettingsDto;
  expiresAt: string | null;
  revokedAt: string | null;
  lastSeenAt: string | null;
  createdBy: string | null;
  createdAt: string;
}

/** Na zoveel minuten zonder ophalen telt een scherm als offline (het scherm haalt elke minuut op). */
export const SCREEN_ONLINE_MINUTES = 5;

// ---------- tijdelijke banner ----------

const dateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Gebruik jjjj-mm-dd");

/** Tijdelijke banner bovenaan alle schermen van het gebouw, van startsOn tot en met endsOn. */
export const announcementSchema = z
  .object({
    text: z.string().trim().min(1, "Vul een tekst in").max(140),
    startsOn: dateSchema,
    endsOn: dateSchema,
  })
  .refine((a) => a.startsOn <= a.endsOn, { message: "De laatste dag ligt voor de eerste", path: ["endsOn"] });

export interface AnnouncementDto {
  id: string;
  text: string;
  startsOn: string;
  endsOn: string;
  createdBy: string | null;
}

// ---------- openbare pagina /s/<token> ----------

export type WeatherKind = "clear" | "partly" | "cloudy" | "fog" | "drizzle" | "rain" | "snow" | "storm";

export interface WeatherDto {
  temperature: number;
  kind: WeatherKind;
  /** "Lichte regen", "Half bewolkt" … */
  text: string;
  windBft: number;
  /** Windrichting als "ZW". */
  windDir: string;
  /** Neerslag in mm, 8 waarden van rainMinutes elk: per kwartier (2 uur) of per uur (8 uur). */
  rain: number[];
  rainMinutes: 15 | 60;
  /** Bron voor de vermelding onderaan: "Open-Meteo" of "MET Norway". */
  source: string;
  /** Verwachting per paar uur. */
  hours: { time: string; temperature: number; kind: WeatherKind }[];
}

export interface ScreenVisitDto {
  /** Eerste en laatste dag van de afspraak (yyyy-mm-dd). */
  startOn: string;
  endOn: string;
  title: string;
  /** Lobby: soort bedrijf ("Leverancier · Liften"); Beheer: naam van bedrijf of uitvoerder. */
  who: string | null;
  confirmed: boolean;
  kind: "keuring" | "onderhoud" | "taak" | "meter";
}

export interface ScreenIssueDto {
  id: string;
  title: string;
  priority: Priority;
  /** Monteur of leverancier ingeschakeld. */
  inProgress: boolean;
  since: string;
}

export interface ScreenTaskDto {
  id: string;
  code: string;
  title: string;
  who: string | null;
  dueOn: string;
}

export interface ScreenOverdueDto {
  id: string;
  title: string;
  asset: string | null;
  dueOn: string;
}

export interface ScreenReportDto {
  id: string;
  title: string;
  channel: string;
  createdAt: string;
}

export interface PublicScreenDto {
  org: string;
  building: { name: string; address: string | null };
  profile: ScreenProfile;
  settings: ScreenSettingsDto;
  /** Datum van vandaag in Nederland (yyyy-mm-dd), zodat het scherm niet op een verkeerde klok leunt. */
  today: string;
  generatedAt: string;
  /** Banners die vandaag lopen. */
  announcements: { id: string; text: string; endsOn: string }[];
  visits: ScreenVisitDto[];
  issues: ScreenIssueDto[];
  reports: {
    today: number;
    resolvedThisMonth: number;
    /** Alleen Beheer. */
    toReview?: number;
    openIssues?: number;
    latest?: ScreenReportDto[];
  };
  /** Alleen Beheer. */
  tasks?: ScreenTaskDto[];
  overdue?: ScreenOverdueDto[];
  /** null: weer staat uit of is even niet beschikbaar. */
  weather: WeatherDto | null;
}
